import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, Loader2, Check, X, Ban, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATE_COLOR = {
  REQUESTED: "bg-amber-100 text-amber-800",
  CONNECTED: "bg-emerald-600 text-white",
  DECLINED:  "bg-stone-200 text-stone-600",
  BLOCKED:   "bg-rose-100 text-rose-800",
  NONE:      "bg-stone-100 text-stone-500",
};

/** P6.3 · Connections page. Manage incoming/outgoing connection edges. */
export default function ConnectionsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [me, setMe]     = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [r, m] = await Promise.all([
        api.get("/social/connections"),
        api.get("/users/me").catch(() => ({ data: null })),
      ]);
      setRows(r.data.items || []);
      setMe(m.data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load connections");
    } finally { setBusy(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, otherId) => {
    try {
      await api.post(`/social/connect/${path}`, { to_user_id: otherId });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="connections-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="connections-back">
            <ArrowLeft size={18}/>
          </button>
          <UserPlus size={16} className="text-emerald-700"/>
          <div className="font-black text-stone-900">Connections</div>
          <div className="ml-auto text-[11px] text-stone-500 font-bold" data-testid="connections-count">
            {rows.length}
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {!busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="connections-empty">
            No connections yet. Send a connection request to another user to get started.
          </div>
        )}
        {rows.map((r) => {
          const other = r.other_user_id;
          const inbound = r.state === "REQUESTED" && r.requested_by !== me?.user_id;
          const outbound = r.state === "REQUESTED" && r.requested_by === me?.user_id;
          return (
            <div key={r.connection_id}
                 className="w-full bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3"
                 data-testid={`connection-row-${r.connection_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-black text-stone-900 text-sm truncate">{other}</div>
                <div className="text-[10px] mt-0.5">
                  <span className={`px-1.5 py-0.5 rounded-full uppercase font-black ${STATE_COLOR[r.state] || STATE_COLOR.NONE}`}>
                    {r.state}
                  </span>
                  {outbound && <span className="ml-2 text-stone-400">sent</span>}
                  {inbound && <span className="ml-2 text-amber-700">wants to connect</span>}
                </div>
              </div>
              {inbound && (
                <>
                  <button onClick={() => act("accept", other)} data-testid={`conn-accept-${r.connection_id}`}
                          className="px-3 py-1.5 rounded-full bg-emerald-600 text-white font-black text-[11px] inline-flex items-center gap-1">
                    <Check size={12}/> Accept
                  </button>
                  <button onClick={() => act("decline", other)} data-testid={`conn-decline-${r.connection_id}`}
                          className="px-2 py-1.5 rounded-full bg-stone-100 text-stone-700 font-black text-[11px]">
                    <X size={12}/>
                  </button>
                </>
              )}
              {outbound && (
                <button onClick={() => act("cancel", other)} data-testid={`conn-cancel-${r.connection_id}`}
                        className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 font-black text-[11px]">Cancel</button>
              )}
              {r.state === "CONNECTED" && (
                <>
                  <button onClick={async () => {
                    try {
                      const resp = await api.post("/social/connect/open-thread", { to_user_id: other });
                      const conv = resp.data?.conversation;
                      if (conv?.conv_id) nav(`/messages#${conv.conv_id}`);
                    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open thread"); }
                  }} data-testid={`conn-message-${r.connection_id}`}
                          className="px-3 py-1.5 rounded-full bg-emerald-600 text-white font-black text-[11px] inline-flex items-center gap-1">
                    <MessageCircle size={12}/> Message
                  </button>
                  <button onClick={() => act("remove", other)} data-testid={`conn-remove-${r.connection_id}`}
                          className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 font-black text-[11px]">Remove</button>
                </>
              )}
              {r.state !== "BLOCKED" && (
                <button onClick={() => act("block", other)} data-testid={`conn-block-${r.connection_id}`}
                        className="p-1.5 rounded-full text-rose-600 hover:bg-rose-50">
                  <Ban size={12}/>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
